import { useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { Icon } from '@/new/components/icon';

const DRIP_INTERVAL_MS = 100;

interface ReasoningTempWindowProps {
  items: string[];
  totalCount: number;
}

export function ReasoningTempWindow({ items, totalCount }: ReasoningTempWindowProps) {
  const [displayIndex, setDisplayIndex] = useState(0);

  useEffect(() => {
    if (displayIndex >= items.length - 1) return;

    const timeout = setTimeout(() => {
      setDisplayIndex((prev) => prev + 1);
    }, DRIP_INTERVAL_MS);

    return () => clearTimeout(timeout);
  }, [displayIndex, items.length]);

  const visibleItem = items[Math.min(displayIndex, items.length - 1)];

  if (!visibleItem) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} className="my-4">
      <div className="font-inter mb-1.5 ml-2 flex items-center gap-1 text-xs text-gray-500">
        <Icon className="lucide--search" />
        <p>
          Analyzing {totalCount} matching {totalCount === 1 ? 'fact or document' : 'facts or documents'}...
        </p>
      </div>

      <div className="flex h-9 items-center gap-2.5 overflow-hidden rounded-lg border border-gray-200 bg-white px-3">
        <div className="relative h-5 min-w-0 flex-1">
          <AnimatePresence initial={false}>
            <motion.p
              key={displayIndex}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="font-inter absolute inset-0 truncate text-xs leading-5 text-gray-800"
            >
              {visibleItem}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
